import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";

import JobApplicationList from "../components/JobApplicationList";
import LoadButton from "../components/LoadButton";
import Client from "../api/Client";
import emptyAll from "../images/empty-all.png";
import emptyFavorites from "../images/empty-favorites.png";
import emptyHidden from "../images/empty-hidden.png";

const propTypes = {
    job_application_list: PropTypes.array.isRequired,
    has_more: PropTypes.bool.isRequired,
    page: PropTypes.number.isRequired
};

const customStyles = {
    content : {
        top         : '50%',
        left        : '50%',
        right       : 'auto',
        bottom      : 'auto',
        marginRight : '-50%',
        transform   : 'translate(-50%, -50%)'
    }
};

class JobDashboard extends React.Component {
    state = {
        jobId: this.props.job_id,
        jobApplicationList: this.props.job_application_list || [],
        hasMore: this.props.has_more,
        fetchingData: false,
        page: this.props.page || 1,
        modalIsOpen: false,
        currentJobApplication: null,
        tab: 'all',
        layout: 'table',
        tabLists: {
            all: this.props.job_application_list || [],
            favorites: null,
            hidden: null
        },
        tabMeta: {
            all: { hasMore: this.props.has_more, page: this.props.page || 1 },
            favorites: { hasMore: false, page: 1 },
            hidden: { hasMore: false, page: 1 }
        }
    };

    fetchSeq = 0;

    componentDidMount() {
        try {
            const stored = window.localStorage.getItem('videebop.applicantsLayout');
            if (stored === 'scan') {
                this.setState({ layout: 'compact' });
            } else if (['table', 'compact', 'grid'].includes(stored)) {
                this.setState({ layout: stored });
            }
        } catch (e) {}
    }

    setLayout = (layout) => {
        this.setState({ layout });
        try {
            window.localStorage.setItem('videebop.applicantsLayout', layout);
        } catch (e) {}
    };

    fetchApplications = (more = true) => {
        const seq = ++this.fetchSeq;
        const filter = this.state.tab;
        this.setState({ fetchingData: true });
        const params = new URLSearchParams();
        params.set('page', (more ? this.state.page : 1));
        if (filter !== 'all') params.set('filter', filter);

        Client.getJobApplications(this.state.jobId, params)
            .then(response => {
                if (seq !== this.fetchSeq) return;
                const data = response.data;
                const list = data.job_applications.data;

                this.setState((state) => ({
                    jobApplicationList: list,
                    hasMore: data.has_more,
                    page: data.next_page,
                    fetchingData: false,
                    tabLists: { ...state.tabLists, [filter]: list },
                    tabMeta: {
                        ...state.tabMeta,
                        [filter]: { hasMore: data.has_more, page: data.next_page }
                    }
                }));
            }).catch((error) => {
                if (seq !== this.fetchSeq) return;
                console.log(error);
                this.setState({ fetchingData: false });
            });
    };

    onLoadMore = () => {
        this.fetchApplications(true);
    };

    openModal = (currentJobApplication) => {
        this.setState({modalIsOpen: true, currentJobApplication});
    };

    closeModal = () => {
        this.setState({modalIsOpen: false});
    };

    handleTabChange = (event, tab) => {
      event.preventDefault();
      if (tab === this.state.tab) return;

      const cached = this.state.tabLists[tab];
      const meta = this.state.tabMeta[tab] || { hasMore: false, page: 1 };

      this.setState({
        tab,
        fetchingData: true,
        jobApplicationList: cached || [],
        hasMore: cached ? meta.hasMore : false,
        page: 1
      }, () => {
        this.fetchApplications(false);
      });
    }

    emptyCopy() {
        if (this.state.tab === 'favorites') {
            return {
                title: 'No favorites yet',
                body: 'Star applicants on the All tab to keep the ones you want to revisit here.'
            };
        }
        if (this.state.tab === 'hidden') {
            return {
                title: 'Nothing hidden',
                body: 'Applicants you hide are tucked away here until you unhide them.'
            };
        }
        return {
            title: 'No applicants yet',
            body: 'Share your VideeBop link and video responses will show up here.'
        };
    }

    render() {
        const { tab, jobApplicationList, fetchingData, layout } = this.state;
        const isEmpty = jobApplicationList.length === 0;
        const empty = this.emptyCopy();
        const layouts = [
            {
                id: 'table',
                label: 'Table',
                icon: (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <rect x="1.5" y="2" width="13" height="12" rx="1.5" />
                        <path d="M1.5 6.25h13M6.25 6.25v7.75" />
                    </svg>
                )
            },
            {
                id: 'compact',
                label: 'Compact',
                icon: (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <rect x="1.5" y="2" width="4" height="3" rx="0.5" />
                        <path d="M7.25 3.5H14.5" />
                        <rect x="1.5" y="6.5" width="4" height="3" rx="0.5" />
                        <path d="M7.25 8H14.5" />
                        <rect x="1.5" y="11" width="4" height="3" rx="0.5" />
                        <path d="M7.25 12.5H14.5" />
                    </svg>
                )
            },
            {
                id: 'grid',
                label: 'Grid',
                icon: (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <rect x="1.5" y="1.5" width="5.25" height="5.25" rx="0.75" />
                        <rect x="9.25" y="1.5" width="5.25" height="5.25" rx="0.75" />
                        <rect x="1.5" y="9.25" width="5.25" height="5.25" rx="0.75" />
                        <rect x="9.25" y="9.25" width="5.25" height="5.25" rx="0.75" />
                    </svg>
                )
            },
        ];
        return (
            <div>
                <div className="applicants-toolbar">
                  <div className="applicants-tab" role="tablist">
                    <button type="button" role="tab" aria-selected={tab === 'all'} className={`applicants-tab--item ${tab === 'all' ? 'active' : ''}`} onClick={(e) => {this.handleTabChange(e, 'all')}}>
                      All
                    </button>
                    <button type="button" role="tab" aria-selected={tab === 'favorites'} className={`applicants-tab--item ${tab === 'favorites' ? 'active' : ''}`} onClick={(e) => {this.handleTabChange(e, 'favorites')}}>
                      Favorites
                    </button>
                    <button type="button" role="tab" aria-selected={tab === 'hidden'} className={`applicants-tab--item ${tab === 'hidden' ? 'active' : ''}`} onClick={(e) => {this.handleTabChange(e, 'hidden')}}>
                      Hidden
                    </button>
                  </div>
                  <div className="applicants-layout" role="radiogroup" aria-label="Applicant layout">
                    {layouts.map((item) => (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={layout === item.id}
                        key={item.id}
                        className={`applicants-layout__btn${layout === item.id ? ' is-active' : ''}`}
                        title={item.label}
                        aria-label={item.label}
                        onClick={() => this.setLayout(item.id)}
                      >
                        {item.icon}
                        <span className="applicants-layout__label">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div id="responsive-table" className={`applicants-view applicants-view--${layout}`} aria-busy={fetchingData}>
                    <JobApplicationList
                        jobApplications={jobApplicationList}
                        onModalOpen={this.openModal}
                        allowToggle={true}
                        layout={layout}
                        empty={isEmpty ? (
                            <div className="applicants-empty" data-test="applicants-empty">
                                <img
                                    className="applicants-empty__art"
                                    src={tab === 'favorites' ? emptyFavorites : tab === 'hidden' ? emptyHidden : emptyAll}
                                    alt=""
                                />
                                <h3 className="applicants-empty__title">{empty.title}</h3>
                                <p className="applicants-empty__body">{empty.body}</p>
                            </div>
                        ) : null}
                    />
                </div>

                <div className="text-center my-5">
                    <LoadButton
                        onLoadMore={this.onLoadMore}
                        show={this.state.hasMore && !isEmpty}
                        fetching={this.state.fetchingData}
                    />
                </div>

                <Modal
                    isOpen={this.state.modalIsOpen}
                    onRequestClose={this.closeModal}
                    style={customStyles}
                    ariaHideApp={false}
                >
                    <p>
                      Share {this.state.currentJobApplication ? (` ${this.state.currentJobApplication.attributes.applicant.name}'s `) : ('')} application:
                    </p>
                    <div className="card mb-2">
                        <div className="card-body p-2 small">
                            <span data-test="share-info" id="share-info">
                                {this.state.currentJobApplication ? (
                                    this.state.currentJobApplication.attributes.share_job_application_url
                                ) : (
                                    ''
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="text-right">
                        <button onClick={this.closeModal} className="btn btn-sm btn-outline mr-2">
                            Close
                        </button>
                        <button id="copy-button" data-clipboard-target="#share-info" className="btn btn-sm btn-success">
                            Copy
                        </button>
                    </div>
                </Modal>
            </div>
        )
    }
}

JobDashboard.propTypes = propTypes;

export default JobDashboard;
